import { BriefcaseIcon } from "lucide-react";

import { ExternalGrantStatusEnum } from "@/__generated__/graphql";
import { Icon } from "@/components/atoms/Icon";
import { EnumColors } from "@/constants/values";
import { State } from "@/state";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";

import { useExternalGrants, useOpenClientBooks } from "./ExternalGrants.hooks";

/**
 * Across the top of the whole app, in yellow, while a client's books are
 * opened - the same in light and dark, so it is never mistaken for the
 * accountant's own: whose books they are, and the way back to their clients.
 * Once the client has taken their books back, it says so - the books read
 * nothing then.
 */
export function ExternalGrantsBanner() {
  const externalGrantIdentifiers = State(
    (state) => state.externalGrantIdentifiers,
  );
  if (externalGrantIdentifiers.length === 0) {
    return null;
  }
  return (
    <ExternalGrantsBannerBar
      externalGrantIdentifiers={externalGrantIdentifiers}
    />
  );
}

function ExternalGrantsBannerBar({
  externalGrantIdentifiers,
}: {
  externalGrantIdentifiers: string[];
}) {
  const { received, isLoading } = useExternalGrants();
  const openClientBooks = useOpenClientBooks();
  // The client's businesses opened that are shared still.
  const standing = received.filter(
    (one) =>
      externalGrantIdentifiers.includes(one.identifier) &&
      one.grantStatus === ExternalGrantStatusEnum.Active,
  );
  const grant = standing[0];

  return (
    <div
      data-testid="external-grants-banner"
      className="flex items-center gap-3 pl-3 pr-3 min-h-9 shrink-0 [border-bottom-width:1px] [border-bottom-style:solid] border-input"
      style={{
        backgroundColor: EnumColors.YELLOW.solidColor,
        color: EnumColors.GREY_1.solidColor,
      }}
    >
      <div className="flex">
        <Icon ReactIcon={BriefcaseIcon} size="sm" />
      </div>
      <Typography
        level="body-sm"
        className="truncate flex-1 min-w-0 text-inherit font-medium"
      >
        <Skeleton loading={isLoading} variant="inline">
          {grant
            ? `${grant.sharedByName ?? grant.sharedByEmailAddress}'s books - you're working in them for ${grant.granteeName}.`
            : "Your access to these books has ended."}
        </Skeleton>{" "}
        {/* The way back, as words after what it says: underlined, a button. */}
        <button
          type="button"
          data-testid="external-grants-banner-exit"
          onClick={() => openClientBooks([])}
          className="[font:inherit] text-inherit [background:none] [border:none] p-0 cursor-pointer underline underline-offset-2"
        >
          Back to your clients
        </button>
      </Typography>
    </div>
  );
}
